import { useEffect, useState, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Container, Row, Col, Card, CardHeader, CardBody, CardFooter,
  Table, Badge, Progress, Button, Input, InputGroup, InputGroupAddon, InputGroupText,
  Spinner, Pagination, PaginationItem, PaginationLink,
  Nav, NavItem, NavLink, TabContent, TabPane,
  Modal, ModalHeader, ModalBody, ModalFooter, Alert
} from 'reactstrap';
import classnames from 'classnames';
import { Bar, Doughnut, Line } from 'react-chartjs-2';
import {
  fetchFullLengthExams,
  fetchEnhancedAnalyticsData,
  fetchExamQuestionAnalytics,
  fetchStudentExamDetails,
  clearEnhancedAnalytics
} from '../../../redux/QuestionBank/FullLengthExamAction';
import { fetchSubjects, fetchUnits, fetchTopics } from '../../../redux/QuestionBank/SubjectsUnitsAction';
import { draftRawToHtml } from '../../../util/draftToHtml';

const PASSING_SCORE = 70;

const FullLengthExamAnalyticsPage = () => {
  const dispatch = useDispatch();

  // Redux state
  const { exams, examsLoading } = useSelector(state => state.fullLengthExams || {});
  const { enhancedAnalytics, enhancedAnalyticsLoading } = useSelector(state => state.fullLengthExams || {});
  const { examQuestionAnalytics, examQuestionAnalyticsLoading } = useSelector(state => state.fullLengthExams || {});
  const { studentExamDetails, studentExamDetailsLoading } = useSelector(state => state.fullLengthExams || {});
  const { subjects } = useSelector(state => state.questionBankSubjects || { subjects: [] });
  const { units } = useSelector(state => state.questionBankUnits || { units: [] });
  const { topics } = useSelector(state => state.questionBankTopics || { topics: [] });

  // Tab & filter state
  const [activeTab, setActiveTab] = useState('overview');
  const [dateRange, setDateRange] = useState({ start: '', end: '' });
  const [selectedExamId, setSelectedExamId] = useState('');
  const [scoreFilterExam, setScoreFilterExam] = useState('all');

  // Student tab state
  const [studentSearch, setStudentSearch] = useState('');
  const [studentSortField, setStudentSortField] = useState('avgScore');
  const [studentSortDir, setStudentSortDir] = useState('desc');
  const [studentPage, setStudentPage] = useState(1);
  const [studentPerPage] = useState(20);
  const [studentModalOpen, setStudentModalOpen] = useState(false);

  // Question view modal
  const [viewQuestion, setViewQuestion] = useState(null);

  // Pagination
  const [examPage, setExamPage] = useState(1);
  const [examPerPage] = useState(10);
  const [questionPage, setQuestionPage] = useState(1);
  const [questionPerPage] = useState(10);
  const [distractorPage, setDistractorPage] = useState(1);
  const [distractorPerPage] = useState(5);

  const loading = examsLoading || enhancedAnalyticsLoading;

  // ===========================
  // DATA FETCHING
  // ===========================
  useEffect(() => {
    dispatch(fetchFullLengthExams());
    dispatch(fetchEnhancedAnalyticsData());
    dispatch(fetchSubjects());
    dispatch(fetchUnits());
    dispatch(fetchTopics());
    return () => { dispatch(clearEnhancedAnalytics()); };
  }, [dispatch]);

  useEffect(() => {
    if (selectedExamId) {
      setQuestionPage(1);
      setDistractorPage(1);
      dispatch(fetchExamQuestionAnalytics(selectedExamId));
    }
  }, [selectedExamId, dispatch]);

  // ===========================
  // HELPERS
  // ===========================
  const formatTime = (seconds) => {
    if (!seconds && seconds !== 0) return 'N/A';
    if (seconds < 60) return `${seconds}s`;
    const minutes = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${minutes}m ${secs}s`;
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A';
    return new Date(timestamp).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const getScoreColor = (score) => {
    if (score >= 80) return 'success';
    if (score >= 60) return 'warning';
    return 'danger';
  };

  const getDifficultyBadge = (correctPct) => {
    if (correctPct >= 80) return { label: 'Easy', color: 'success' };
    if (correctPct >= 60) return { label: 'Medium', color: 'info' };
    if (correctPct >= 40) return { label: 'Hard', color: 'warning' };
    return { label: 'Very Hard', color: 'danger' };
  };

  const getDiscriminationBadge = (index) => {
    if (index === null || index === undefined) return { label: 'N/A', color: 'secondary' };
    if (index >= 0.4) return { label: 'Excellent', color: 'success' };
    if (index >= 0.3) return { label: 'Good', color: 'info' };
    if (index >= 0.2) return { label: 'Fair', color: 'warning' };
    return { label: 'Poor', color: 'danger' };
  };

  const getSubjectName = (subjectId) => {
    const s = subjects?.find(x => x.subjectId === subjectId);
    if (!s) return subjectId || 'Unknown';
    return s.name;
  };

  const getUnitName = (unitId) => {
    const u = units?.find(x => x.unitId === unitId);
    if (!u) return unitId || '-';
    return u.name;
  };

  const getTopicName = (topicId) => {
    if (!topicId) return '-';
    const t = topics?.find(x => x.topicId === topicId);
    if (!t) return topicId;
    return t.name;
  };

  const getPageNumbers = (currentPage, totalPages) => {
    const pages = [];
    const maxVisible = 5;
    if (totalPages <= maxVisible) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
    } else if (currentPage <= 3) {
      for (let i = 1; i <= 4; i++) pages.push(i);
      pages.push('...', totalPages);
    } else if (currentPage >= totalPages - 2) {
      pages.push(1, '...');
      for (let i = totalPages - 3; i <= totalPages; i++) pages.push(i);
    } else {
      pages.push(1, '...');
      for (let i = currentPage - 1; i <= currentPage + 1; i++) pages.push(i);
      pages.push('...', totalPages);
    }
    return pages;
  };

  const handleRefresh = () => {
    dispatch(fetchFullLengthExams());
    dispatch(fetchEnhancedAnalyticsData());
    if (selectedExamId) dispatch(fetchExamQuestionAnalytics(selectedExamId));
  };

  const handleViewStudent = async (userHandle) => {
    const result = await dispatch(fetchStudentExamDetails(userHandle));
    if (result.success) {
      setStudentModalOpen(true);
    }
  };

  const handleStudentSort = (field) => {
    if (studentSortField === field) {
      setStudentSortDir(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setStudentSortField(field);
      setStudentSortDir('desc');
    }
    setStudentPage(1);
  };

  const setDatePreset = (days) => {
    if (!days) {
      setDateRange({ start: '', end: '' });
      return;
    }
    const end = new Date();
    const start = new Date();
    start.setDate(start.getDate() - days);
    setDateRange({
      start: start.toISOString().split('T')[0],
      end: end.toISOString().split('T')[0]
    });
  };

  // ===========================
  // COMPUTED: Date-filtered data
  // ===========================
  const dateFilteredAttempts = useMemo(() => {
    if (!enhancedAnalytics?.allAttempts) return [];
    let filtered = enhancedAnalytics.allAttempts.filter(a => a.status === 'completed');
    if (dateRange.start) {
      const startMs = new Date(dateRange.start).getTime();
      filtered = filtered.filter(a => a.completedAt >= startMs);
    }
    if (dateRange.end) {
      const endMs = new Date(dateRange.end + 'T23:59:59').getTime();
      filtered = filtered.filter(a => a.completedAt <= endMs);
    }
    return filtered;
  }, [enhancedAnalytics, dateRange]);

  const allAttemptsInRange = useMemo(() => {
    if (!enhancedAnalytics?.allAttempts) return [];
    let filtered = enhancedAnalytics.allAttempts;
    if (dateRange.start) {
      const startMs = new Date(dateRange.start).getTime();
      filtered = filtered.filter(a => (a.completedAt || a.startedAt) >= startMs);
    }
    if (dateRange.end) {
      const endMs = new Date(dateRange.end + 'T23:59:59').getTime();
      filtered = filtered.filter(a => (a.completedAt || a.startedAt) <= endMs);
    }
    return filtered;
  }, [enhancedAnalytics, dateRange]);

  // ===========================
  // COMPUTED: Overall stats
  // ===========================
  const overallStats = useMemo(() => {
    const ca = dateFilteredAttempts;
    if (!ca.length) return { totalAttempts: 0, avgScore: 0, passRate: 0, avgCorrect: 0, avgIncorrect: 0, avgOmitted: 0, uniqueStudents: 0 };
    const avgScore = Math.round(ca.reduce((s, a) => s + (a.score || 0), 0) / ca.length);
    const passCount = ca.filter(a => a.score >= PASSING_SCORE).length;
    const passRate = Math.round((passCount / ca.length) * 100);
    const avgCorrect = Math.round(ca.reduce((s, a) => s + (a.correctAnswers || 0), 0) / ca.length);
    const avgIncorrect = Math.round(ca.reduce((s, a) => s + (a.incorrectAnswers || 0), 0) / ca.length);
    const avgOmitted = Math.round(ca.reduce((s, a) => s + (a.omittedAnswers || 0), 0) / ca.length);
    const uniqueStudents = new Set(ca.map(a => a.userHandle)).size;
    return { totalAttempts: ca.length, avgScore, passRate, avgCorrect, avgIncorrect, avgOmitted, uniqueStudents };
  }, [dateFilteredAttempts]);

  const completionRate = useMemo(() => {
    const total = allAttemptsInRange.length;
    const completed = allAttemptsInRange.filter(a => a.status === 'completed').length;
    return { started: total, completed, rate: total > 0 ? Math.round((completed / total) * 100) : 0 };
  }, [allAttemptsInRange]);

  const avgTimeUsed = useMemo(() => {
    const ca = dateFilteredAttempts.filter(a => a.timeLimit && a.timeRemaining !== undefined);
    if (!ca.length) return 0;
    return Math.round(ca.reduce((s, a) => s + (a.timeLimit - (a.timeRemaining || 0)), 0) / ca.length);
  }, [dateFilteredAttempts]);

  // ===========================
  // COMPUTED: Exam performance
  // ===========================
  const examPerformance = useMemo(() => {
    if (!dateFilteredAttempts.length) return [];
    const byExam = {};
    dateFilteredAttempts.forEach(a => {
      if (!byExam[a.examId]) byExam[a.examId] = { examId: a.examId, examName: a.examName, attempts: [] };
      byExam[a.examId].attempts.push(a);
    });
    return Object.values(byExam).map(exam => {
      const scores = exam.attempts.map(a => a.score || 0);
      const passCount = exam.attempts.filter(a => a.score >= PASSING_SCORE).length;
      const timeCa = exam.attempts.filter(a => a.timeLimit && a.timeRemaining !== undefined);
      const avgTimeUsedExam = timeCa.length > 0 ? Math.round(timeCa.reduce((s, a) => s + (a.timeLimit - (a.timeRemaining || 0)), 0) / timeCa.length) : 0;
      return {
        examId: exam.examId, examName: exam.examName,
        totalAttempts: exam.attempts.length,
        avgScore: Math.round(scores.reduce((s, v) => s + v, 0) / scores.length),
        passRate: Math.round((passCount / exam.attempts.length) * 100),
        bestScore: Math.max(...scores), worstScore: Math.min(...scores),
        avgTimeUsed: avgTimeUsedExam,
      };
    }).sort((a, b) => b.totalAttempts - a.totalAttempts);
  }, [dateFilteredAttempts]);

  // Score distribution
  const scoreDistribution = useMemo(() => {
    const filtered = scoreFilterExam === 'all' ? dateFilteredAttempts : dateFilteredAttempts.filter(a => a.examId === scoreFilterExam);
    const bands = [
      { label: '0-49%', min: 0, max: 49 }, { label: '50-59%', min: 50, max: 59 },
      { label: '60-69%', min: 60, max: 69 }, { label: '70-79%', min: 70, max: 79 },
      { label: '80-89%', min: 80, max: 89 }, { label: '90-100%', min: 90, max: 100 },
    ];
    return { labels: bands.map(b => b.label), counts: bands.map(b => filtered.filter(a => (a.score || 0) >= b.min && (a.score || 0) <= b.max).length) };
  }, [dateFilteredAttempts, scoreFilterExam]);

  // Answer breakdown
  const answerBreakdown = useMemo(() => {
    if (!dateFilteredAttempts.length) return { correct: 0, incorrect: 0, omitted: 0 };
    const filtered = scoreFilterExam === 'all' ? dateFilteredAttempts : dateFilteredAttempts.filter(a => a.examId === scoreFilterExam);
    return {
      correct: filtered.reduce((s, a) => s + (a.correctAnswers || 0), 0),
      incorrect: filtered.reduce((s, a) => s + (a.incorrectAnswers || 0), 0),
      omitted: filtered.reduce((s, a) => s + (a.omittedAnswers || 0), 0),
    };
  }, [dateFilteredAttempts, scoreFilterExam]);

  // Score trend
  const scoreTrend = useMemo(() => {
    if (!dateFilteredAttempts.length) return { labels: [], data: [], counts: [] };
    const sorted = [...dateFilteredAttempts].filter(a => a.completedAt).sort((a, b) => a.completedAt - b.completedAt);
    if (!sorted.length) return { labels: [], data: [], counts: [] };
    const byWeek = {};
    sorted.forEach(a => {
      const d = new Date(a.completedAt);
      const weekStart = new Date(d); weekStart.setDate(d.getDate() - d.getDay());
      const key = weekStart.toISOString().split('T')[0];
      if (!byWeek[key]) byWeek[key] = { scores: [], date: weekStart };
      byWeek[key].scores.push(a.score || 0);
    });
    const weeks = Object.keys(byWeek).sort();
    return {
      labels: weeks.map(k => new Date(byWeek[k].date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })),
      data: weeks.map(k => Math.round(byWeek[k].scores.reduce((s, v) => s + v, 0) / byWeek[k].scores.length)),
      counts: weeks.map(k => byWeek[k].scores.length),
    };
  }, [dateFilteredAttempts]);

  // ===========================
  // COMPUTED: Engagement metrics
  // ===========================
  const engagementMetrics = useMemo(() => {
    if (!dateFilteredAttempts.length) return { weekLabels: [], weeklyCounts: [], weeklyStudents: [] };
    const byWeek = {};
    dateFilteredAttempts.forEach(a => {
      const d = new Date(a.completedAt);
      const weekStart = new Date(d); weekStart.setDate(d.getDate() - d.getDay());
      const key = weekStart.toISOString().split('T')[0];
      if (!byWeek[key]) byWeek[key] = { date: weekStart, count: 0, students: new Set() };
      byWeek[key].count++;
      byWeek[key].students.add(a.userHandle);
    });
    const weeks = Object.keys(byWeek).sort();
    return {
      weekLabels: weeks.map(k => new Date(byWeek[k].date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })),
      weeklyCounts: weeks.map(k => byWeek[k].count),
      weeklyStudents: weeks.map(k => byWeek[k].students.size),
    };
  }, [dateFilteredAttempts]);

  // ===========================
  // COMPUTED: Retake analytics
  // ===========================
  const retakeAnalytics = useMemo(() => {
    if (!dateFilteredAttempts.length) return [];
    const byExamUser = {};
    dateFilteredAttempts.forEach(a => {
      const key = `${a.examId}|||${a.userHandle}`;
      if (!byExamUser[key]) byExamUser[key] = { examId: a.examId, examName: a.examName, attempts: [] };
      byExamUser[key].attempts.push(a);
    });
    const byExam = {};
    Object.values(byExamUser).forEach(eu => {
      if (eu.attempts.length < 2) return;
      const sorted = [...eu.attempts].sort((a, b) => a.completedAt - b.completedAt);
      if (!byExam[eu.examId]) byExam[eu.examId] = { examId: eu.examId, examName: eu.examName, firsts: [], subsequents: [] };
      byExam[eu.examId].firsts.push(sorted[0].score);
      sorted.slice(1).forEach(a => byExam[eu.examId].subsequents.push(a.score));
    });
    return Object.values(byExam).map(e => {
      const firstAvg = Math.round(e.firsts.reduce((s, v) => s + v, 0) / e.firsts.length);
      const subAvg = Math.round(e.subsequents.reduce((s, v) => s + v, 0) / e.subsequents.length);
      return { examId: e.examId, examName: e.examName, firstAttemptAvg: firstAvg, subsequentAvg: subAvg, improvement: subAvg - firstAvg, retakeCount: e.subsequents.length, studentsRetaking: e.firsts.length };
    }).sort((a, b) => b.retakeCount - a.retakeCount);
  }, [dateFilteredAttempts]);

  // ===========================
  // COMPUTED: Time management per exam
  // ===========================
  const timeManagement = useMemo(() => {
    if (!dateFilteredAttempts.length) return [];
    const byExam = {};
    dateFilteredAttempts.forEach(a => {
      if (!a.timeLimit || a.timeRemaining === undefined) return;
      if (!byExam[a.examId]) byExam[a.examId] = { examId: a.examId, examName: a.examName, attempts: [] };
      const pctUsed = Math.round(((a.timeLimit - (a.timeRemaining || 0)) / a.timeLimit) * 100);
      byExam[a.examId].attempts.push({ pctUsed, timeLimit: a.timeLimit, timeRemaining: a.timeRemaining });
    });
    return Object.values(byExam).map(e => {
      const pcts = e.attempts.map(a => a.pctUsed);
      return {
        examId: e.examId, examName: e.examName, totalAttempts: e.attempts.length,
        avgPctUsed: Math.round(pcts.reduce((s, v) => s + v, 0) / pcts.length),
        usedOver90: e.attempts.filter(a => a.pctUsed > 90).length,
        usedUnder50: e.attempts.filter(a => a.pctUsed < 50).length,
      };
    });
  }, [dateFilteredAttempts]);

  // ===========================
  // COMPUTED: Student performance
  // ===========================
  const studentPerformance = useMemo(() => {
    if (!dateFilteredAttempts.length) return [];
    const byStudent = {};
    dateFilteredAttempts.forEach(a => {
      if (!byStudent[a.userHandle]) {
        byStudent[a.userHandle] = {
          userHandle: a.userHandle, userName: a.userName || a.userHandle, userEmail: a.userEmail || '',
          profilePicture: a.profilePicture, accountType: a.accountType, planId: a.planId,
          attempts: []
        };
      }
      byStudent[a.userHandle].attempts.push(a);
    });
    return Object.values(byStudent).map(s => {
      const scores = s.attempts.map(a => a.score || 0);
      const avg = Math.round(scores.reduce((sum, v) => sum + v, 0) / scores.length);
      const passCount = s.attempts.filter(a => a.score >= PASSING_SCORE).length;
      return {
        ...s, examsTaken: s.attempts.length, avgScore: avg,
        highestScore: Math.max(...scores), lowestScore: Math.min(...scores),
        passRate: Math.round((passCount / s.attempts.length) * 100),
        lastExamDate: Math.max(...s.attempts.map(a => a.completedAt || 0)),
      };
    });
  }, [dateFilteredAttempts]);

  // Struggling students (enhanced with profile info)
  const strugglingStudents = useMemo(() => {
    return studentPerformance.filter(s => s.avgScore < PASSING_SCORE).sort((a, b) => a.avgScore - b.avgScore).slice(0, 10);
  }, [studentPerformance]);

  // Filtered & sorted students
  const filteredStudents = useMemo(() => {
    let list = studentPerformance;
    if (studentSearch) {
      const term = studentSearch.toLowerCase();
      list = list.filter(s => s.userName.toLowerCase().includes(term) || s.userEmail.toLowerCase().includes(term) || s.userHandle.toLowerCase().includes(term));
    }
    list.sort((a, b) => {
      const dir = studentSortDir === 'asc' ? 1 : -1;
      if (typeof a[studentSortField] === 'string') return a[studentSortField].localeCompare(b[studentSortField]) * dir;
      return ((a[studentSortField] || 0) - (b[studentSortField] || 0)) * dir;
    });
    return list;
  }, [studentPerformance, studentSearch, studentSortField, studentSortDir]);

  // Question analytics
  const analyticsAttemptCount = examQuestionAnalytics?.totalAttempts || 0;
  const sortedQuestions = useMemo(() => {
    const questions = examQuestionAnalytics?.questions || [];
    return [...questions].sort((a, b) => a.correctPct - b.correctPct);
  }, [examQuestionAnalytics]);
  const sortedSubjects = useMemo(() => {
    const subs = examQuestionAnalytics?.subjectStats || [];
    return [...subs].sort((a, b) => a.avgCorrectPct - b.avgCorrectPct);
  }, [examQuestionAnalytics]);

  // Distractor analysis — questions with notable distractors
  const distractorQuestions = useMemo(() => {
    if (!examQuestionAnalytics?.distractorAnalysis || !analyticsAttemptCount) return [];
    const qList = examQuestionAnalytics.questions || [];
    return qList.filter(q => {
      const da = examQuestionAnalytics.distractorAnalysis[q.questionId];
      if (!da) return false;
      return da.choices.some(c => !c.isCorrect && c.selectedCount > 0);
    });
  }, [examQuestionAnalytics, analyticsAttemptCount]);

  // ===========================
  // PAGINATION COMPUTED
  // ===========================
  const examTotalPages = Math.ceil(examPerformance.length / examPerPage);
  const examStartIndex = (examPage - 1) * examPerPage;
  const paginatedExams = examPerformance.slice(examStartIndex, examStartIndex + examPerPage);

  const questionTotalPages = Math.ceil(sortedQuestions.length / questionPerPage);
  const questionStartIndex = (questionPage - 1) * questionPerPage;
  const paginatedQuestions = sortedQuestions.slice(questionStartIndex, questionStartIndex + questionPerPage);

  const studentTotalPages = Math.ceil(filteredStudents.length / studentPerPage);
  const studentStartIndex = (studentPage - 1) * studentPerPage;
  const paginatedStudents = filteredStudents.slice(studentStartIndex, studentStartIndex + studentPerPage);

  const distractorTotalPages = Math.ceil(distractorQuestions.length / distractorPerPage);
  const distractorStartIndex = (distractorPage - 1) * distractorPerPage;
  const paginatedDistractors = distractorQuestions.slice(distractorStartIndex, distractorStartIndex + distractorPerPage);

  // ===========================
  // CHART CONFIGS (Chart.js v2)
  // ===========================
  const scoreBarData = {
    labels: scoreDistribution.labels,
    datasets: [{ label: 'Students', data: scoreDistribution.counts, backgroundColor: ['#f5365c', '#fb6340', '#fb6340', '#11cdef', '#2dce89', '#2dce89'], borderWidth: 0, barPercentage: 0.7 }]
  };
  const scoreBarOptions = { responsive: true, maintainAspectRatio: false, legend: { display: false }, tooltips: { callbacks: { label: (item) => `${item.yLabel} attempts` } }, scales: { yAxes: [{ ticks: { beginAtZero: true, precision: 0 }, gridLines: { color: 'rgba(0,0,0,0.05)' } }], xAxes: [{ gridLines: { display: false } }] } };

  const doughnutData = { labels: ['Correct', 'Incorrect', 'Omitted'], datasets: [{ data: [answerBreakdown.correct, answerBreakdown.incorrect, answerBreakdown.omitted], backgroundColor: ['#2dce89', '#f5365c', '#fb6340'], borderWidth: 2, borderColor: '#fff' }] };
  const doughnutOptions = { responsive: true, maintainAspectRatio: false, cutoutPercentage: 65, legend: { display: true, position: 'bottom', labels: { boxWidth: 12, padding: 15 } }, tooltips: { callbacks: { label: (item, data) => { const val = data.datasets[0].data[item.index]; const total = data.datasets[0].data.reduce((s, v) => s + v, 0); const pct = total > 0 ? Math.round((val / total) * 100) : 0; return ` ${data.labels[item.index]}: ${val.toLocaleString()} (${pct}%)`; } } } };

  const trendLineData = {
    labels: scoreTrend.labels,
    datasets: [
      { label: 'Avg Score', data: scoreTrend.data, borderColor: '#5e72e4', backgroundColor: 'rgba(94, 114, 228, 0.1)', fill: true, lineTension: 0.4, pointRadius: 4, pointBackgroundColor: '#5e72e4', borderWidth: 2 },
      { label: 'Passing Threshold', data: scoreTrend.labels.map(() => PASSING_SCORE), borderColor: 'rgba(45, 206, 137, 0.5)', borderDash: [5, 5], fill: false, pointRadius: 0, borderWidth: 2 }
    ]
  };
  const trendLineOptions = { responsive: true, maintainAspectRatio: false, legend: { display: true, position: 'top', labels: { boxWidth: 12 } }, tooltips: { mode: 'index', intersect: false, callbacks: { afterLabel: (item) => item.datasetIndex === 0 ? `${scoreTrend.counts[item.index]} attempts` : '' } }, scales: { yAxes: [{ ticks: { beginAtZero: false, min: 0, max: 100, stepSize: 10 }, gridLines: { color: 'rgba(0,0,0,0.05)' }, scaleLabel: { display: true, labelString: 'Score %' } }], xAxes: [{ gridLines: { display: false }, scaleLabel: { display: true, labelString: 'Week' } }] } };

  const engagementBarData = {
    labels: engagementMetrics.weekLabels,
    datasets: [
      { label: 'Attempts', data: engagementMetrics.weeklyCounts, backgroundColor: 'rgba(94, 114, 228, 0.7)', borderWidth: 0, barPercentage: 0.6 },
      { label: 'Unique Students', data: engagementMetrics.weeklyStudents, backgroundColor: 'rgba(45, 206, 137, 0.7)', borderWidth: 0, barPercentage: 0.6 }
    ]
  };
  const engagementBarOptions = { responsive: true, maintainAspectRatio: false, legend: { display: true, position: 'top', labels: { boxWidth: 12 } }, tooltips: { mode: 'index', intersect: false }, scales: { yAxes: [{ ticks: { beginAtZero: true, precision: 0 }, gridLines: { color: 'rgba(0,0,0,0.05)' } }], xAxes: [{ gridLines: { display: false } }] } };

  // ===========================
  // RENDER HELPERS
  // ===========================
  const renderPagination = (currentPage, totalPages, setPage, totalItems, perPage, startIndex) => {
    if (totalPages <= 1) return null;
    return (
      <CardFooter className="py-3">
        <Row className="align-items-center">
          <Col xs="12" sm="6" className="mb-2 mb-sm-0"><small className="text-muted">Showing {startIndex + 1}-{Math.min(startIndex + perPage, totalItems)} of {totalItems}</small></Col>
          <Col xs="12" sm="6">
            <Pagination className="pagination justify-content-start justify-content-sm-end mb-0" listClassName="justify-content-start justify-content-sm-end mb-0" style={{ flexWrap: 'wrap' }}>
              <PaginationItem disabled={currentPage === 1}><PaginationLink onClick={() => setPage(currentPage - 1)}><i className="fas fa-angle-left" /></PaginationLink></PaginationItem>
              {getPageNumbers(currentPage, totalPages).map((page, idx) => (
                <PaginationItem key={idx} active={page === currentPage} disabled={page === '...'}><PaginationLink onClick={() => page !== '...' && setPage(page)}>{page}</PaginationLink></PaginationItem>
              ))}
              <PaginationItem disabled={currentPage === totalPages}><PaginationLink onClick={() => setPage(currentPage + 1)}><i className="fas fa-angle-right" /></PaginationLink></PaginationItem>
            </Pagination>
          </Col>
        </Row>
      </CardFooter>
    );
  };

  const renderProfilePic = (pic, name, size = 32) => {
    if (pic) {
      return <img src={pic} alt="" className="rounded-circle mr-2" style={{ width: `${size}px`, height: `${size}px`, objectFit: 'cover' }} />;
    }
    return (
      <div className="rounded-circle bg-gradient-primary text-white d-flex align-items-center justify-content-center mr-2" style={{ width: `${size}px`, height: `${size}px`, fontSize: `${Math.round(size * 0.44)}px` }}>
        {(name || '?').charAt(0).toUpperCase()}
      </div>
    );
  };

  const renderSortIcon = (field) => {
    if (studentSortField !== field) return <i className="fas fa-sort ml-1 text-muted" style={{ fontSize: '10px' }} />;
    return <i className={`fas fa-sort-${studentSortDir === 'asc' ? 'up' : 'down'} ml-1 text-primary`} style={{ fontSize: '10px' }} />;
  };

  // ===========================
  // TAB 1: OVERVIEW
  // ===========================
  const renderOverviewTab = () => (
    <>
      <Row className="mb-4">
        <Col lg="7" className="mb-4 mb-lg-0">
          <Card className="shadow h-100">
            <CardHeader className="border-0">
              <Row className="align-items-center">
                <Col xs className="mb-1 mb-sm-0"><h3 className="mb-0">Score Distribution</h3></Col>
                <Col xs="12" sm="auto">
                  <Input type="select" bsSize="sm" value={scoreFilterExam} onChange={(e) => setScoreFilterExam(e.target.value)} style={{ maxWidth: '200px' }}>
                    <option value="all">All Exams</option>
                    {examPerformance.map(e => (<option key={e.examId} value={e.examId}>{e.examName}</option>))}
                  </Input>
                </Col>
              </Row>
            </CardHeader>
            <CardBody>
              {loading ? <div className="text-center py-4"><Spinner color="primary" /></div> :
                dateFilteredAttempts.length === 0 ? <div className="text-center py-4 text-muted">No data yet</div> :
                  <div style={{ position: 'relative', height: '280px' }}><Bar data={scoreBarData} options={scoreBarOptions} /></div>}
            </CardBody>
          </Card>
        </Col>
        <Col lg="5">
          <Card className="shadow h-100">
            <CardHeader className="border-0"><h3 className="mb-0">Answer Breakdown</h3></CardHeader>
            <CardBody>
              {loading || dateFilteredAttempts.length === 0 ? <div className="text-center py-4">{loading ? <Spinner color="primary" /> : <span className="text-muted">No data yet</span>}</div> : (
                <>
                  <div style={{ position: 'relative', height: '220px' }}><Doughnut data={doughnutData} options={doughnutOptions} /></div>
                  <div className="text-center mt-3"><small className="text-muted">Total: {(answerBreakdown.correct + answerBreakdown.incorrect + answerBreakdown.omitted).toLocaleString()} answers</small></div>
                </>
              )}
            </CardBody>
          </Card>
        </Col>
      </Row>

      {scoreTrend.labels.length > 1 && (
        <Card className="shadow mb-4">
          <CardHeader className="border-0">
            <h3 className="mb-0"><i className="fas fa-chart-line text-primary mr-2"></i>Score Trend Over Time</h3>
            <small className="text-muted">Weekly average scores across all exams</small>
          </CardHeader>
          <CardBody><div style={{ position: 'relative', height: '300px' }}><Line data={trendLineData} options={trendLineOptions} /></div></CardBody>
        </Card>
      )}

      {engagementMetrics.weekLabels.length > 0 && (
        <Card className="shadow mb-4">
          <CardHeader className="border-0">
            <h3 className="mb-0"><i className="fas fa-chart-area text-info mr-2"></i>Activity & Engagement</h3>
            <small className="text-muted">Weekly exam attempts and unique students</small>
          </CardHeader>
          <CardBody><div style={{ position: 'relative', height: '280px' }}><Bar data={engagementBarData} options={engagementBarOptions} /></div></CardBody>
        </Card>
      )}
    </>
  );

  // ===========================
  // TAB 2: EXAMS
  // ===========================
  const renderExamsTab = () => (
    <>
      <Card className="shadow mb-4">
        <CardHeader className="border-0"><h3 className="mb-0">Exam Performance</h3></CardHeader>
        <CardBody className="p-0">
          {loading ? <div className="text-center py-5"><Spinner color="primary" /></div> :
            examPerformance.length === 0 ? <div className="text-center py-5 text-muted">No exam data yet</div> : (
              <Table className="align-items-center table-flush" responsive>
                <thead className="thead-light">
                  <tr>
                    <th style={{ minWidth: '140px' }}>Exam</th>
                    <th className="text-center" style={{ minWidth: '70px' }}>Attempts</th>
                    <th className="text-center" style={{ minWidth: '80px' }}>Avg Score</th>
                    <th className="text-center" style={{ minWidth: '100px' }}>Pass Rate</th>
                    <th className="text-center" style={{ minWidth: '60px' }}>Best</th>
                    <th className="text-center" style={{ minWidth: '60px' }}>Worst</th>
                    <th className="text-center" style={{ minWidth: '80px' }}>Avg Time</th>
                    <th style={{ minWidth: '50px' }}></th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedExams.map(exam => (
                    <tr key={exam.examId}>
                      <td><strong>{exam.examName}</strong></td>
                      <td className="text-center">{exam.totalAttempts}</td>
                      <td className="text-center"><Badge color={getScoreColor(exam.avgScore)}>{exam.avgScore}%</Badge></td>
                      <td className="text-center" style={{ minWidth: '100px' }}>
                        <div className="d-flex align-items-center justify-content-center">
                          <span className="mr-2" style={{ fontSize: '12px' }}>{exam.passRate}%</span>
                          <Progress value={exam.passRate} color={getScoreColor(exam.passRate)} style={{ height: '6px', width: '50px' }} />
                        </div>
                      </td>
                      <td className="text-center"><Badge color="success">{exam.bestScore}%</Badge></td>
                      <td className="text-center"><Badge color={getScoreColor(exam.worstScore)}>{exam.worstScore}%</Badge></td>
                      <td className="text-center"><span className="text-muted">{formatTime(exam.avgTimeUsed)}</span></td>
                      <td><Button color="primary" size="sm" outline onClick={() => { setSelectedExamId(exam.examId); setActiveTab('questions'); }} title="View question analytics"><i className="fas fa-microscope"></i></Button></td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            )}
        </CardBody>
        {renderPagination(examPage, examTotalPages, setExamPage, examPerformance.length, examPerPage, examStartIndex)}
      </Card>

      {retakeAnalytics.length > 0 && (
        <Card className="shadow mb-4">
          <CardHeader className="border-0">
            <h3 className="mb-0"><i className="fas fa-redo text-success mr-2"></i>Retake Performance</h3>
            <small className="text-muted">How students improve across retakes of the same exam</small>
          </CardHeader>
          <CardBody className="p-0">
            <Table className="align-items-center table-flush" responsive>
              <thead className="thead-light">
                <tr>
                  <th>Exam</th>
                  <th className="text-center">Students Retaking</th>
                  <th className="text-center">1st Attempt Avg</th>
                  <th className="text-center">Subsequent Avg</th>
                  <th className="text-center">Improvement</th>
                  <th className="text-center">Total Retakes</th>
                </tr>
              </thead>
              <tbody>
                {retakeAnalytics.map(e => (
                  <tr key={e.examId}>
                    <td><strong>{e.examName}</strong></td>
                    <td className="text-center">{e.studentsRetaking}</td>
                    <td className="text-center"><Badge color={getScoreColor(e.firstAttemptAvg)}>{e.firstAttemptAvg}%</Badge></td>
                    <td className="text-center"><Badge color={getScoreColor(e.subsequentAvg)}>{e.subsequentAvg}%</Badge></td>
                    <td className="text-center">
                      {e.improvement > 0 ? <span className="text-success font-weight-bold">+{e.improvement}%</span> :
                        e.improvement < 0 ? <span className="text-danger font-weight-bold">{e.improvement}%</span> :
                          <span className="text-muted">0%</span>}
                    </td>
                    <td className="text-center">{e.retakeCount}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </CardBody>
        </Card>
      )}

      {timeManagement.length > 0 && (
        <Card className="shadow mb-4">
          <CardHeader className="border-0">
            <h3 className="mb-0"><i className="fas fa-hourglass-half text-warning mr-2"></i>Time Management</h3>
            <small className="text-muted">How students manage their exam time</small>
          </CardHeader>
          <CardBody className="p-0">
            <Table className="align-items-center table-flush" responsive>
              <thead className="thead-light">
                <tr>
                  <th>Exam</th>
                  <th className="text-center">Attempts</th>
                  <th className="text-center">Avg Time Used</th>
                  <th className="text-center">Used &gt;90%</th>
                  <th className="text-center">Used &lt;50%</th>
                </tr>
              </thead>
              <tbody>
                {timeManagement.map(e => (
                  <tr key={e.examId}>
                    <td><strong>{e.examName}</strong></td>
                    <td className="text-center">{e.totalAttempts}</td>
                    <td className="text-center">
                      <Badge color={e.avgPctUsed > 90 ? 'danger' : e.avgPctUsed > 75 ? 'warning' : 'success'}>{e.avgPctUsed}%</Badge>
                    </td>
                    <td className="text-center">
                      {e.usedOver90 > 0 ? <span className="text-warning font-weight-bold">{e.usedOver90} ({Math.round((e.usedOver90 / e.totalAttempts) * 100)}%)</span> : <span className="text-muted">0</span>}
                    </td>
                    <td className="text-center">
                      {e.usedUnder50 > 0 ? <span className="text-info">{e.usedUnder50} ({Math.round((e.usedUnder50 / e.totalAttempts) * 100)}%)</span> : <span className="text-muted">0</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </CardBody>
        </Card>
      )}
    </>
  );

  // ===========================
  // TAB 3: STUDENTS
  // ===========================
  const renderStudentsTab = () => (
    <>
      <Card className="shadow mb-4">
        <CardHeader className="border-0">
          <Row className="align-items-center">
            <Col xs="12" md className="mb-2 mb-md-0"><h3 className="mb-0"><i className="fas fa-users text-primary mr-2"></i>Student Performance</h3><small className="text-muted">{filteredStudents.length} students</small></Col>
            <Col xs="12" md="5" lg="4">
              <InputGroup>
                <InputGroupAddon addonType="prepend"><InputGroupText><i className="fas fa-search" /></InputGroupText></InputGroupAddon>
                <Input placeholder="Search by name or email..." value={studentSearch} onChange={(e) => { setStudentSearch(e.target.value); setStudentPage(1); }} />
              </InputGroup>
            </Col>
          </Row>
        </CardHeader>
        <CardBody className="p-0">
          {loading ? <div className="text-center py-5"><Spinner color="primary" /></div> :
            filteredStudents.length === 0 ? <div className="text-center py-5 text-muted">No student data</div> : (
              <Table className="align-items-center table-flush" responsive>
                <thead className="thead-light">
                  <tr>
                    <th style={{ cursor: 'pointer', minWidth: '180px' }} onClick={() => handleStudentSort('userName')}>Student {renderSortIcon('userName')}</th>
                    <th className="text-center" style={{ cursor: 'pointer', minWidth: '65px' }} onClick={() => handleStudentSort('examsTaken')}>Exams {renderSortIcon('examsTaken')}</th>
                    <th className="text-center" style={{ cursor: 'pointer', minWidth: '85px' }} onClick={() => handleStudentSort('avgScore')}>Avg Score {renderSortIcon('avgScore')}</th>
                    <th className="text-center" style={{ cursor: 'pointer', minWidth: '70px' }} onClick={() => handleStudentSort('highestScore')}>Highest {renderSortIcon('highestScore')}</th>
                    <th className="text-center" style={{ cursor: 'pointer', minWidth: '70px' }} onClick={() => handleStudentSort('lowestScore')}>Lowest {renderSortIcon('lowestScore')}</th>
                    <th className="text-center" style={{ cursor: 'pointer', minWidth: '90px' }} onClick={() => handleStudentSort('passRate')}>Pass Rate {renderSortIcon('passRate')}</th>
                    <th className="text-center" style={{ cursor: 'pointer', minWidth: '100px' }} onClick={() => handleStudentSort('lastExamDate')}>Last Exam {renderSortIcon('lastExamDate')}</th>
                    <th style={{ minWidth: '50px' }}></th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedStudents.map(s => (
                    <tr key={s.userHandle}>
                      <td>
                        <div className="d-flex align-items-center">
                          {renderProfilePic(s.profilePicture, s.userName)}
                          <div>
                            <span className="font-weight-bold">{s.userName}</span>
                            <small className="d-block text-muted">{s.userEmail}</small>
                          </div>
                        </div>
                      </td>
                      <td className="text-center">{s.examsTaken}</td>
                      <td className="text-center"><Badge color={getScoreColor(s.avgScore)}>{s.avgScore}%</Badge></td>
                      <td className="text-center"><Badge color="success">{s.highestScore}%</Badge></td>
                      <td className="text-center"><Badge color={getScoreColor(s.lowestScore)}>{s.lowestScore}%</Badge></td>
                      <td className="text-center" style={{ minWidth: '90px' }}>
                        <div className="d-flex align-items-center justify-content-center">
                          <span className="mr-2" style={{ fontSize: '12px' }}>{s.passRate}%</span>
                          <Progress value={s.passRate} color={getScoreColor(s.passRate)} style={{ height: '6px', width: '40px' }} />
                        </div>
                      </td>
                      <td className="text-center"><span className="text-muted">{formatDate(s.lastExamDate)}</span></td>
                      <td><Button color="primary" size="sm" outline onClick={() => handleViewStudent(s.userHandle)} title="View details"><i className="fas fa-eye"></i></Button></td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            )}
        </CardBody>
        {renderPagination(studentPage, studentTotalPages, setStudentPage, filteredStudents.length, studentPerPage, studentStartIndex)}
      </Card>

      {strugglingStudents.length > 0 && (
        <Card className="shadow mb-4">
          <CardHeader className="border-0">
            <h3 className="mb-0"><i className="fas fa-exclamation-triangle text-warning mr-2"></i>Students Needing Attention</h3>
            <small className="text-muted">Students averaging below {PASSING_SCORE}% — sorted by lowest score</small>
          </CardHeader>
          <CardBody className="p-0">
            <Table className="align-items-center table-flush" responsive>
              <thead className="thead-light">
                <tr><th>Student</th><th className="text-center">Exams Taken</th><th className="text-center">Avg Score</th><th className="text-center">Lowest Score</th><th className="text-center">Last Attempt</th><th></th></tr>
              </thead>
              <tbody>
                {strugglingStudents.map(s => (
                  <tr key={s.userHandle}>
                    <td>
                      <div className="d-flex align-items-center">
                        {renderProfilePic(s.profilePicture, s.userName)}
                        <div>
                          <span className="font-weight-bold">{s.userName}</span>
                          <small className="d-block text-muted">{s.userEmail}</small>
                        </div>
                      </div>
                    </td>
                    <td className="text-center">{s.examsTaken}</td>
                    <td className="text-center"><Badge color={getScoreColor(s.avgScore)}>{s.avgScore}%</Badge></td>
                    <td className="text-center"><Badge color="danger">{s.lowestScore}%</Badge></td>
                    <td className="text-center"><span className="text-muted">{formatDate(s.lastExamDate)}</span></td>
                    <td><Button color="primary" size="sm" outline onClick={() => handleViewStudent(s.userHandle)}><i className="fas fa-eye"></i></Button></td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </CardBody>
        </Card>
      )}
    </>
  );

  // ===========================
  // TAB 4: QUESTIONS
  // ===========================
  const renderQuestionsTab = () => (
    <>
      {/* Exam selector */}
      <Card className="shadow mb-4">
        <CardBody>
          <Row className="align-items-center">
            <Col xs="12" md="auto" className="mb-2 mb-md-0"><h3 className="mb-0"><i className="fas fa-microscope text-primary mr-2"></i>Question Analytics</h3></Col>
            <Col xs="12" md="5" lg="4" className="mb-2 mb-md-0">
              <InputGroup>
                <InputGroupAddon addonType="prepend"><InputGroupText><i className="fas fa-filter" /></InputGroupText></InputGroupAddon>
                <Input type="select" value={selectedExamId} onChange={(e) => setSelectedExamId(e.target.value)}>
                  <option value="">Select an exam...</option>
                  {exams?.map(e => (<option key={e.examId} value={e.examId}>{e.name}</option>))}
                </Input>
              </InputGroup>
            </Col>
            {selectedExamId && analyticsAttemptCount > 0 && <Col xs="auto"><small className="text-muted">{analyticsAttemptCount} completed attempts</small></Col>}
          </Row>
        </CardBody>
      </Card>

      {!selectedExamId ? (
        <Card className="shadow mb-5">
          <CardBody className="text-center py-5">
            <i className="fas fa-hand-pointer fa-3x text-muted mb-3 d-block"></i>
            <h5 className="text-muted">Select an exam above to view question analytics</h5>
          </CardBody>
        </Card>
      ) : examQuestionAnalyticsLoading ? (
        <div className="text-center py-5"><Spinner color="primary" /><p className="mt-2 text-muted">Analyzing questions...</p></div>
      ) : (
        <>
          {/* Subject Performance */}
          {sortedSubjects.length > 0 && (
            <Card className="shadow mb-4">
              <CardHeader className="border-0">
                <h3 className="mb-0"><i className="fas fa-book text-info mr-2"></i>Subject Performance</h3>
                <small className="text-muted">How students performed across subjects</small>
              </CardHeader>
              <CardBody className="p-0">
                <Table className="align-items-center table-flush" responsive>
                  <thead className="thead-light">
                    <tr><th style={{ minWidth: '130px' }}>Subject</th><th className="text-center" style={{ minWidth: '70px' }}>Questions</th><th className="text-center" style={{ minWidth: '90px' }}>Avg Correct %</th><th style={{ minWidth: '150px' }}>Performance</th><th className="text-center" style={{ minWidth: '80px' }}>Difficulty</th></tr>
                  </thead>
                  <tbody>
                    {sortedSubjects.map(subject => {
                      const diff = getDifficultyBadge(subject.avgCorrectPct);
                      return (
                        <tr key={subject.subjectId}>
                          <td><strong>{subject.subjectName || getSubjectName(subject.subjectId)}</strong></td>
                          <td className="text-center">{subject.total}</td>
                          <td className="text-center"><Badge color={getScoreColor(subject.avgCorrectPct)}>{subject.avgCorrectPct}%</Badge></td>
                          <td>
                            <Progress multi style={{ height: '8px' }}>
                              <Progress bar color="success" value={subject.total > 0 ? (subject.correctSum / (subject.total * analyticsAttemptCount)) * 100 : 0} />
                              <Progress bar color="danger" value={subject.total > 0 ? (subject.incorrectSum / (subject.total * analyticsAttemptCount)) * 100 : 0} />
                              <Progress bar color="warning" value={subject.total > 0 ? (subject.omittedSum / (subject.total * analyticsAttemptCount)) * 100 : 0} />
                            </Progress>
                            <div style={{ fontSize: '10px' }} className="mt-1">
                              <span className="text-success mr-2">C:{subject.correctSum}</span>
                              <span className="text-danger mr-2">I:{subject.incorrectSum}</span>
                              <span className="text-warning">O:{subject.omittedSum}</span>
                            </div>
                          </td>
                          <td className="text-center"><Badge color={diff.color}>{diff.label}</Badge></td>
                        </tr>
                      );
                    })}
                  </tbody>
                </Table>
              </CardBody>
            </Card>
          )}

          {/* Question Difficulty */}
          <Card className="shadow mb-4">
            <CardHeader className="border-0">
              <h3 className="mb-0"><i className="fas fa-sort-amount-up text-warning mr-2"></i>Question Difficulty</h3>
              <small className="text-muted">Sorted by difficulty (hardest first)</small>
            </CardHeader>
            <CardBody className="p-0">
              {sortedQuestions.length === 0 ? <Alert color="warning" className="m-4"><i className="fas fa-search mr-2"></i>No questions match the current filters</Alert> : (
                <Table className="align-items-center table-flush" responsive>
                  <thead className="thead-light">
                    <tr><th>#</th><th>Subject</th><th>Unit</th><th>Topic</th><th className="text-center">Correct %</th><th className="text-center">Avg Time</th><th className="text-center">Flagged</th><th className="text-center">Difficulty</th><th></th></tr>
                  </thead>
                  <tbody>
                    {paginatedQuestions.map(q => {
                      const diff = getDifficultyBadge(q.correctPct);
                      return (
                        <tr key={q.questionId} className={q.status === 'archived' ? 'table-secondary' : ''}>
                          <td><strong>Q{q.questionNumber}</strong>{q.status === 'archived' && <Badge color="warning" pill className="ml-1" style={{ fontSize: '9px' }}>Archived</Badge>}</td>
                          <td style={{ fontSize: '13px' }}>{q.subjectId ? getSubjectName(q.subjectId) : (q.subjectName || 'Unknown')}</td>
                          <td style={{ fontSize: '13px' }}>{q.unitId ? getUnitName(q.unitId) : (q.unitName || '-')}</td>
                          <td style={{ fontSize: '13px' }}>{q.topicId ? getTopicName(q.topicId) : (q.topicName || '-')}</td>
                          <td className="text-center">
                            <Badge color={getScoreColor(q.correctPct)}>{q.correctPct}%</Badge>
                            <div style={{ fontSize: '10px' }} className="text-muted mt-1">{q.correct}C / {q.incorrect}I / {q.omitted}O</div>
                          </td>
                          <td className="text-center"><span className={q.avgTime > 66 ? 'text-warning font-weight-bold' : 'text-muted'}>{formatTime(q.avgTime)}</span></td>
                          <td className="text-center">{q.flagged > 0 ? <span><i className="fas fa-flag text-warning mr-1"></i>{q.flagged}</span> : <span className="text-muted">-</span>}</td>
                          <td className="text-center"><Badge color={diff.color}>{diff.label}</Badge></td>
                          <td>
                            <Button color="info" size="sm" onClick={() => setViewQuestion(q)} title="View Question">
                              <i className="fas fa-eye"></i>
                            </Button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </Table>
              )}
            </CardBody>
            {renderPagination(questionPage, questionTotalPages, setQuestionPage, sortedQuestions.length, questionPerPage, questionStartIndex)}
          </Card>

          {/* Discrimination Index */}
          {sortedQuestions.length > 0 && sortedQuestions[0].discriminationIndex !== null && (
            <Card className="shadow mb-4">
              <CardHeader className="border-0">
                <h3 className="mb-0"><i className="fas fa-balance-scale text-info mr-2"></i>Discrimination Index</h3>
                <small className="text-muted">How well each question differentiates strong vs weak students (top 27% vs bottom 27%)</small>
              </CardHeader>
              <CardBody className="p-0">
                <Table className="align-items-center table-flush" responsive>
                  <thead className="thead-light">
                    <tr><th>#</th><th>Subject</th><th className="text-center">Correct %</th><th className="text-center">Index</th><th className="text-center">Top 27%</th><th className="text-center">Bottom 27%</th><th className="text-center">Quality</th></tr>
                  </thead>
                  <tbody>
                    {[...sortedQuestions].sort((a, b) => (a.discriminationIndex || 0) - (b.discriminationIndex || 0)).slice(0, 20).map(q => {
                      const disc = getDiscriminationBadge(q.discriminationIndex);
                      return (
                        <tr key={q.questionId} className={q.status === 'archived' ? 'table-secondary' : ''}>
                          <td><strong>Q{q.questionNumber}</strong>{q.status === 'archived' && <Badge color="warning" pill className="ml-1" style={{ fontSize: '9px' }}>Archived</Badge>}</td>
                          <td style={{ fontSize: '13px' }}>{q.subjectId ? getSubjectName(q.subjectId) : (q.subjectName || 'Unknown')}</td>
                          <td className="text-center"><Badge color={getScoreColor(q.correctPct)}>{q.correctPct}%</Badge></td>
                          <td className="text-center"><Badge color={disc.color}>{q.discriminationIndex !== null ? q.discriminationIndex.toFixed(2) : 'N/A'}</Badge></td>
                          <td className="text-center">{q.topGroupCorrectPct !== null ? `${q.topGroupCorrectPct}%` : '-'}</td>
                          <td className="text-center">{q.bottomGroupCorrectPct !== null ? `${q.bottomGroupCorrectPct}%` : '-'}</td>
                          <td className="text-center"><Badge color={disc.color}>{disc.label}</Badge></td>
                        </tr>
                      );
                    })}
                  </tbody>
                </Table>
              </CardBody>
            </Card>
          )}

          {/* Distractor Analysis */}
          {distractorQuestions.length > 0 && (
            <Card className="shadow mb-4">
              <CardHeader className="border-0">
                <h3 className="mb-0"><i className="fas fa-chart-pie text-danger mr-2"></i>Answer Distractor Analysis</h3>
                <small className="text-muted">Which wrong answers are most commonly selected ({distractorQuestions.length} questions)</small>
              </CardHeader>
              <CardBody>
                {paginatedDistractors.map(q => {
                  const da = examQuestionAnalytics?.distractorAnalysis?.[q.questionId];
                  if (!da) return null;
                  return (
                    <div key={q.questionId} className="mb-4 pb-3 border-bottom">
                      <h5 className="mb-2">
                        Q{q.questionNumber} <small className="text-muted ml-2">{q.subjectId ? getSubjectName(q.subjectId) : (q.subjectName || 'Unknown')}</small>
                        <Badge color={getScoreColor(q.correctPct)} className="ml-2">{q.correctPct}% correct</Badge>
                        {q.status === 'archived' && <Badge color="warning" pill className="ml-2" style={{ fontSize: '10px' }}>Archived</Badge>}
                      </h5>
                      <Table size="sm" className="mb-0" responsive>
                        <thead><tr><th style={{ minWidth: '150px' }}>Answer Choice</th><th className="text-center" style={{ width: '80px', minWidth: '70px' }}>Selected</th><th className="text-center" style={{ width: '90px', minWidth: '80px' }}>% Students</th><th className="text-center" style={{ width: '80px', minWidth: '70px' }}></th></tr></thead>
                        <tbody>
                          {da.choices.map(choice => {
                            const pct = analyticsAttemptCount > 0 ? Math.round((choice.selectedCount / analyticsAttemptCount) * 100) : 0;
                            return (
                              <tr key={choice.choiceId} className={choice.isCorrect ? 'table-success' : pct >= 20 && !choice.isCorrect ? 'table-warning' : ''}>
                                <td style={{ fontSize: '13px', maxWidth: '400px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{choice.text ? (choice.text.length > 100 ? choice.text.substring(0, 100) + '...' : choice.text) : '-'}</td>
                                <td className="text-center">{choice.selectedCount}</td>
                                <td className="text-center">{pct}%</td>
                                <td className="text-center">
                                  {choice.isCorrect ? <Badge color="success" pill>Correct</Badge> : pct >= 20 ? <Badge color="warning" pill>Strong Distractor</Badge> : null}
                                </td>
                              </tr>
                            );
                          })}
                          {da.omittedCount > 0 && (
                            <tr className="text-muted"><td><em>Omitted</em></td><td className="text-center">{da.omittedCount}</td><td className="text-center">{analyticsAttemptCount > 0 ? Math.round((da.omittedCount / analyticsAttemptCount) * 100) : 0}%</td><td></td></tr>
                          )}
                        </tbody>
                      </Table>
                    </div>
                  );
                })}
              </CardBody>
              {renderPagination(distractorPage, distractorTotalPages, setDistractorPage, distractorQuestions.length, distractorPerPage, distractorStartIndex)}
            </Card>
          )}
        </>
      )}
    </>
  );

  // ===========================
  // STUDENT DETAIL MODAL
  // ===========================
  const renderStudentModal = () => {
    const details = studentExamDetails;
    if (!details) return null;

    const { userInfo, attempts, scoreHistory, subjectPerformance } = details;
    const avgScore = attempts.length > 0 ? Math.round(attempts.reduce((s, a) => s + (a.score || 0), 0) / attempts.length) : 0;
    const highest = attempts.length > 0 ? Math.max(...attempts.map(a => a.score || 0)) : 0;
    const lowest = attempts.length > 0 ? Math.min(...attempts.map(a => a.score || 0)) : 0;

    const studentScoreTrendData = {
      labels: scoreHistory.map(h => `#${h.attemptNumber}`),
      datasets: [{
        label: 'Score', data: scoreHistory.map(h => h.score),
        borderColor: '#5e72e4', backgroundColor: 'rgba(94, 114, 228, 0.1)',
        fill: true, lineTension: 0.4, pointRadius: 5, pointBackgroundColor: '#5e72e4', borderWidth: 2
      }, {
        label: 'Passing', data: scoreHistory.map(() => PASSING_SCORE),
        borderColor: 'rgba(45, 206, 137, 0.5)', borderDash: [5, 5], fill: false, pointRadius: 0, borderWidth: 2
      }]
    };
    const studentScoreTrendOptions = {
      responsive: true, maintainAspectRatio: false,
      legend: { display: false },
      tooltips: { callbacks: { afterLabel: (item) => item.datasetIndex === 0 ? `${scoreHistory[item.index]?.examName || ''}\n${formatDate(scoreHistory[item.index]?.date)}` : '' } },
      scales: { yAxes: [{ ticks: { min: 0, max: 100, stepSize: 10 }, gridLines: { color: 'rgba(0,0,0,0.05)' } }], xAxes: [{ gridLines: { display: false } }] }
    };

    return (
      <Modal isOpen={studentModalOpen} toggle={() => setStudentModalOpen(false)} size="xl" className="modal-dialog-scrollable">
        <ModalHeader toggle={() => setStudentModalOpen(false)}>
          <div className="d-flex align-items-center">
            {renderProfilePic(userInfo.profilePicture, userInfo.name, 40)}
            <div style={{ minWidth: 0 }}><h3 className="mb-0" style={{ fontSize: 'clamp(1rem, 3vw, 1.25rem)' }}>{userInfo.name}</h3><small className="text-muted text-truncate d-block" style={{ maxWidth: '250px' }}>{userInfo.email}</small></div>
          </div>
        </ModalHeader>
        <ModalBody>
          {studentExamDetailsLoading ? <div className="text-center py-5"><Spinner color="primary" /></div> : (
            <>
              <Row className="mb-4">
                {[
                  { label: 'Exams Taken', value: attempts.length, gradient: 'primary' },
                  { label: 'Avg Score', value: `${avgScore}%`, gradient: 'success' },
                  { label: 'Highest Score', value: `${highest}%`, gradient: 'info' },
                  { label: 'Lowest Score', value: `${lowest}%`, gradient: 'warning' },
                ].map((stat, i) => (
                  <Col xs="6" md="3" key={i}>
                    <Card className={`bg-gradient-${stat.gradient} text-white shadow mb-3`}>
                      <CardBody className="py-3 text-center"><h3 className="text-white mb-0">{stat.value}</h3><small>{stat.label}</small></CardBody>
                    </Card>
                  </Col>
                ))}
              </Row>

              {scoreHistory.length > 1 && (
                <Card className="shadow mb-4">
                  <CardHeader className="border-0"><h4 className="mb-0">Score Progress</h4></CardHeader>
                  <CardBody><div style={{ position: 'relative', height: '250px' }}><Line data={studentScoreTrendData} options={studentScoreTrendOptions} /></div></CardBody>
                </Card>
              )}

              {subjectPerformance.length > 0 && (
                <Card className="shadow mb-4">
                  <CardHeader className="border-0"><h4 className="mb-0">Subject Performance</h4></CardHeader>
                  <CardBody className="p-0">
                    <Table className="align-items-center table-flush" responsive>
                      <thead className="thead-light"><tr><th style={{ minWidth: '120px' }}>Subject</th><th className="text-center" style={{ minWidth: '70px' }}>Questions</th><th className="text-center" style={{ minWidth: '80px' }}>Correct %</th><th style={{ minWidth: '150px' }}>Performance</th></tr></thead>
                      <tbody>
                        {[...subjectPerformance].sort((a, b) => a.correctPct - b.correctPct).map(s => (
                          <tr key={s.subjectId}>
                            <td><strong>{s.subjectName}</strong></td>
                            <td className="text-center">{s.total}</td>
                            <td className="text-center"><Badge color={getScoreColor(s.correctPct)}>{s.correctPct}%</Badge></td>
                            <td>
                              <Progress multi style={{ height: '8px' }}>
                                <Progress bar color="success" value={s.total > 0 ? (s.correct / s.total) * 100 : 0} />
                                <Progress bar color="danger" value={s.total > 0 ? (s.incorrect / s.total) * 100 : 0} />
                                <Progress bar color="warning" value={s.total > 0 ? (s.omitted / s.total) * 100 : 0} />
                              </Progress>
                              <div style={{ fontSize: '10px' }} className="mt-1">
                                <span className="text-success mr-2">C:{s.correct}</span>
                                <span className="text-danger mr-2">I:{s.incorrect}</span>
                                <span className="text-warning">O:{s.omitted}</span>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </CardBody>
                </Card>
              )}

              <Card className="shadow">
                <CardHeader className="border-0"><h4 className="mb-0">Exam History</h4></CardHeader>
                <CardBody className="p-0">
                  <Table className="align-items-center table-flush" responsive>
                    <thead className="thead-light"><tr><th style={{ minWidth: '130px' }}>Exam</th><th className="text-center" style={{ minWidth: '65px' }}>Score</th><th className="text-center" style={{ minWidth: '90px' }}>C / I / O</th><th className="text-center" style={{ minWidth: '80px' }}>Time Left</th><th className="text-center" style={{ minWidth: '100px' }}>Completed</th></tr></thead>
                    <tbody>
                      {attempts.map(a => (
                        <tr key={a.attemptId}>
                          <td>{a.examName}</td>
                          <td className="text-center"><Badge color={getScoreColor(a.score)}>{a.score}%</Badge></td>
                          <td className="text-center">
                            <span className="text-success">{a.correctAnswers}</span> / <span className="text-danger">{a.incorrectAnswers}</span> / <span className="text-warning">{a.omittedAnswers}</span>
                          </td>
                          <td className="text-center">{formatTime(a.timeRemaining)}</td>
                          <td className="text-center">{formatDate(a.completedAt)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </CardBody>
              </Card>
            </>
          )}
        </ModalBody>
        <ModalFooter><Button color="secondary" onClick={() => setStudentModalOpen(false)}>Close</Button></ModalFooter>
      </Modal>
    );
  };

  // ===========================
  // MAIN RENDER
  // ===========================
  return (
    <>
      {/* Header */}
      <div className="header bg-gradient-primary pb-8 pt-5 pt-md-8">
        <Container fluid>
          <div className="header-body">
            <Row className="align-items-center">
              <Col xs="12" md className="mb-2 mb-md-0">
                <h1 className="text-white mb-0" style={{ fontSize: 'clamp(1.25rem, 4vw, 1.75rem)' }}>Full-Length Exam Analytics</h1>
                <p className="text-white mt-0 mb-0" style={{ fontSize: 'clamp(0.75rem, 2.5vw, 0.875rem)' }}>Comprehensive exam results, student performance, and question analysis</p>
              </Col>
              <Col xs="12" md="auto" className="text-md-right">
                <Button color="secondary" size="sm" onClick={handleRefresh} disabled={loading}>
                  <i className={`fas ${loading ? 'fa-spinner fa-spin' : 'fa-sync'} mr-2`}></i>Refresh
                </Button>
              </Col>
            </Row>

            {/* Stats Cards — 2 rows x 3 columns */}
            <Row className="mt-4">
              {[
                { label: 'Total Attempts', value: overallStats.totalAttempts, icon: 'fa-file-alt', gradient: 'info' },
                { label: 'Avg Score', value: `${overallStats.avgScore}%`, icon: 'fa-chart-line', gradient: 'warning' },
                { label: 'Pass Rate', value: `${overallStats.passRate}%`, icon: 'fa-check-circle', gradient: 'success' },
                { label: 'Unique Students', value: overallStats.uniqueStudents, icon: 'fa-users', gradient: 'primary' },
                { label: 'Completion', value: `${completionRate.rate}%`, icon: 'fa-check-double', gradient: 'default', sub: `${completionRate.completed}/${completionRate.started} started` },
                { label: 'Avg Time Used', value: formatTime(avgTimeUsed), icon: 'fa-clock', gradient: 'danger' },
              ].map((stat, i) => (
                <Col xs="6" lg="4" key={i} className="mb-3 mb-lg-4">
                  <Card className="card-stats shadow">
                    <CardBody className="py-3 px-3 px-sm-4">
                      <Row className="align-items-center">
                        <div className="col" style={{ minWidth: 0 }}>
                          <span className="text-uppercase text-muted font-weight-bold d-block" style={{ fontSize: 'clamp(0.6rem, 1.8vw, 0.8rem)', lineHeight: 1.3 }}>{stat.label}</span>
                          <span className="font-weight-bold mb-0 d-block" style={{ fontSize: 'clamp(1.1rem, 3.5vw, 1.5rem)' }}>{loading ? <Spinner size="sm" /> : stat.value}</span>
                          {stat.sub && <small className="text-muted" style={{ fontSize: '0.7rem' }}>{stat.sub}</small>}
                        </div>
                        <Col className="col-auto d-none d-sm-block"><div className={`icon icon-shape bg-gradient-${stat.gradient} text-white rounded-circle shadow`}><i className={`fas ${stat.icon}`} /></div></Col>
                      </Row>
                    </CardBody>
                  </Card>
                </Col>
              ))}
            </Row>
          </div>
        </Container>
      </div>

      {/* Page Content */}
      <Container className="mt--7" fluid>
        {/* Date Range Filter */}
        <Card className="shadow mb-4">
          <CardBody className="py-3">
            <Row className="align-items-center">
              <Col xs="12" md="auto" className="mb-2 mb-md-0"><i className="fas fa-calendar-alt text-primary mr-2"></i><strong>Filter by Date</strong></Col>
              <Col xs="5" sm="4" md="2" className="mb-2 mb-md-0">
                <Input type="date" bsSize="sm" value={dateRange.start} onChange={(e) => setDateRange(prev => ({ ...prev, start: e.target.value }))} />
              </Col>
              <Col xs="1" md="auto" className="px-0 text-center mb-2 mb-md-0"><span className="text-muted">to</span></Col>
              <Col xs="5" sm="4" md="2" className="mb-2 mb-md-0">
                <Input type="date" bsSize="sm" value={dateRange.end} onChange={(e) => setDateRange(prev => ({ ...prev, end: e.target.value }))} />
              </Col>
              <Col xs="auto" className="mb-2 mb-md-0">
                <Button color="light" size="sm" className="mr-1" onClick={() => setDatePreset(7)}>7d</Button>
                <Button color="light" size="sm" className="mr-1" onClick={() => setDatePreset(30)}>30d</Button>
                <Button color="light" size="sm" className="mr-1" onClick={() => setDatePreset(90)}>90d</Button>
                <Button color="secondary" size="sm" onClick={() => setDatePreset(null)}>All</Button>
              </Col>
              {(dateRange.start || dateRange.end) && (
                <Col xs="auto"><small className="text-muted">{dateFilteredAttempts.length} results</small></Col>
              )}
            </Row>
          </CardBody>
        </Card>

        {/* Tab Navigation */}
        <div style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
          <Nav tabs className="mb-4 flex-nowrap">
            {[
              { id: 'overview', icon: 'fa-chart-pie', label: 'Overview' },
              { id: 'exams', icon: 'fa-file-alt', label: 'Exams' },
              { id: 'students', icon: 'fa-users', label: 'Students' },
              { id: 'questions', icon: 'fa-microscope', label: 'Questions' },
            ].map(tab => (
              <NavItem key={tab.id}>
                <NavLink className={classnames({ active: activeTab === tab.id })} onClick={() => setActiveTab(tab.id)} style={{ cursor: 'pointer', whiteSpace: 'nowrap' }}>
                  <i className={`fas ${tab.icon} mr-1 mr-sm-2`}></i><span className="d-none d-sm-inline">{tab.label}</span><span className="d-sm-none" style={{ fontSize: '13px' }}>{tab.label}</span>
                </NavLink>
              </NavItem>
            ))}
          </Nav>
        </div>

        {/* Tab Content */}
        <TabContent activeTab={activeTab}>
          <TabPane tabId="overview">{activeTab === 'overview' && renderOverviewTab()}</TabPane>
          <TabPane tabId="exams">{activeTab === 'exams' && renderExamsTab()}</TabPane>
          <TabPane tabId="students">{activeTab === 'students' && renderStudentsTab()}</TabPane>
          <TabPane tabId="questions">{activeTab === 'questions' && renderQuestionsTab()}</TabPane>
        </TabContent>
      </Container>

      {/* Student Detail Modal */}
      {renderStudentModal()}

      {/* Question View Modal */}
      <Modal isOpen={!!viewQuestion} toggle={() => setViewQuestion(null)} size="lg">
        <ModalHeader toggle={() => setViewQuestion(null)}>
          <span className="mr-3">Q{viewQuestion?.questionNumber}</span>
          <Badge color="primary" className="mr-1">
            {viewQuestion?.subjectId ? getSubjectName(viewQuestion.subjectId) : (viewQuestion?.subjectName || '')}
          </Badge>
          <Badge color="info" className="mr-1">
            {viewQuestion?.unitId ? getUnitName(viewQuestion.unitId) : (viewQuestion?.unitName || '')}
          </Badge>
          {(viewQuestion?.topicId || viewQuestion?.topicName) && (
            <Badge color="success">
              {viewQuestion?.topicId ? getTopicName(viewQuestion.topicId) : viewQuestion?.topicName}
            </Badge>
          )}
        </ModalHeader>
        <ModalBody>
          {viewQuestion && (
            <>
              {/* Analytics badges */}
              <div className="mb-3">
                <Badge color={getScoreColor(viewQuestion.correctPct)} className="mr-2 px-2 py-1">
                  {viewQuestion.correctPct}% Correct
                </Badge>
                <Badge color="light" className="mr-2 px-2 py-1 text-muted">
                  <i className="fas fa-clock mr-1"></i>Avg {formatTime(viewQuestion.avgTime)}
                </Badge>
                {viewQuestion.flagged > 0 && (
                  <Badge color="warning" className="mr-2 px-2 py-1">
                    <i className="fas fa-flag mr-1"></i>{viewQuestion.flagged} flagged
                  </Badge>
                )}
                <Badge color={getDifficultyBadge(viewQuestion.correctPct).color} className="px-2 py-1">
                  {getDifficultyBadge(viewQuestion.correctPct).label}
                </Badge>
              </div>

              {/* Question Text */}
              {viewQuestion.questionText && (
                <div className="mb-4 p-3 rounded" style={{ backgroundColor: '#f8f9fe' }}>
                  <div dangerouslySetInnerHTML={{ __html: (() => {
                    try {
                      if (typeof viewQuestion.questionText === 'string') {
                        return draftRawToHtml(JSON.parse(viewQuestion.questionText));
                      }
                      return draftRawToHtml(viewQuestion.questionText);
                    } catch {
                      return viewQuestion.questionText || '';
                    }
                  })() }} />
                </div>
              )}

              {/* Answer Choices */}
              {viewQuestion.choices?.map((choice, idx) => {
                const letter = String.fromCharCode(65 + idx);
                const isCorrect = viewQuestion.correctAnswerIds?.includes(choice.choiceId);
                const bgColor = isCorrect ? '#d4edda' : '#ffffff';
                const borderColor = isCorrect ? '#28a745' : '#dee2e6';

                let choiceHtml = '';
                try {
                  if (choice.text && typeof choice.text === 'object') {
                    choiceHtml = draftRawToHtml(choice.text);
                  } else if (typeof choice.text === 'string') {
                    choiceHtml = draftRawToHtml(JSON.parse(choice.text));
                  }
                } catch {
                  choiceHtml = choice.text || '';
                }

                return (
                  <div
                    key={choice.choiceId || idx}
                    className="p-2 mb-2 rounded"
                    style={{ backgroundColor: bgColor, border: `1px solid ${borderColor}` }}
                  >
                    <div className="d-flex align-items-start">
                      <div
                        className={`mr-2 rounded-circle d-flex align-items-center justify-content-center flex-shrink-0 ${isCorrect ? 'bg-success text-white' : 'bg-light'}`}
                        style={{ width: '28px', height: '28px', minWidth: '28px', fontSize: '0.8rem' }}
                      >
                        <strong>{letter}</strong>
                      </div>
                      <div className="flex-grow-1" style={{ fontSize: '0.9rem' }} dangerouslySetInnerHTML={{ __html: choiceHtml }} />
                      {isCorrect && (
                        <div className="ml-2 flex-shrink-0">
                          <i className="fas fa-check text-success"></i>
                          <small className="text-success ml-1">Correct</small>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}

              {/* Explanation */}
              {viewQuestion.explanationText && (() => {
                let html = '';
                try {
                  if (typeof viewQuestion.explanationText === 'string') {
                    html = draftRawToHtml(JSON.parse(viewQuestion.explanationText));
                  } else {
                    html = draftRawToHtml(viewQuestion.explanationText);
                  }
                } catch {
                  html = typeof viewQuestion.explanationText === 'string' ? viewQuestion.explanationText : '';
                }
                if (!html || html === '<p></p>' || html === '<p><br></p>') return null;
                return (
                  <div className="mt-4 p-3 rounded" style={{ backgroundColor: '#fff8e1' }}>
                    <h6 className="mb-2"><i className="fas fa-lightbulb mr-2 text-warning"></i>Explanation</h6>
                    <div dangerouslySetInnerHTML={{ __html: html }} />
                  </div>
                );
              })()}
            </>
          )}
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={() => setViewQuestion(null)}>Close</Button>
        </ModalFooter>
      </Modal>
    </>
  );
};

export default FullLengthExamAnalyticsPage;
